'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const ALLOWED_EVENTS = ['ORDER', 'SUBSCRIPTION'] as const
type AllowedEvent = typeof ALLOWED_EVENTS[number]

const triplewhaleFormSchema = z.object({
  apiKey: z.string().min(1).trim(),
  allowedEvents: z.array(z.enum(ALLOWED_EVENTS)).nullable(),
})

type FormValues = z.infer<typeof triplewhaleFormSchema>

interface IntegrationDefaultValues {
  enabled?: boolean
  credentials?: {
    apiKey?: string
    allowedEvents?: AllowedEvent[] | null
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface TriplewhaleFormProps {
  defaultValues?: IntegrationDefaultValues
  onSubmit: (data: Record<string, unknown>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function TriplewhaleForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: TriplewhaleFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(triplewhaleFormSchema),
    mode: 'all',
    defaultValues: {
      apiKey: (defaultValues?.credentials?.apiKey as string) ?? '',
      allowedEvents: (defaultValues?.credentials?.allowedEvents as AllowedEvent[] | null) ?? [...ALLOWED_EVENTS],
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { allowedEvents, apiKey } = data
    await onSubmit({
      credentials: {
        apiKey,
        allowedEvents: allowedEvents ?? [...ALLOWED_EVENTS],
      },
    })
  }

  const EVENTS: Record<AllowedEvent, string> = {
    ORDER: 'Placed Order',
    SUBSCRIPTION: 'Subscription updated',
  }

  const allowedEvents = watch('allowedEvents')

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Event Support Info */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{t('triplewhale.eventSupport')}</AlertTitle>
        <AlertDescription>
          <div className="space-y-2">
            <p className="text-sm">{t('triplewhale.eventsDescription')}</p>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {Object.entries(EVENTS).map(([key, value]) => (
                <li key={key}>{value}</li>
              ))}
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      {/* API Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('triplewhale.apiConfiguration')}</CardTitle>
          <CardDescription>{t('triplewhale.apiConfigDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">
              {t('triplewhale.apiKey')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={t('triplewhale.apiKeyPlaceholder')}
              disabled={isLoading}
              {...register('apiKey')}
            />
            {errors.apiKey && (
              <p className="text-xs text-destructive">{t('triplewhale.apiKeyRequired')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Event Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('triplewhale.eventSelection')}</CardTitle>
          <CardDescription>{t('triplewhale.eventSelectionDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
          {Object.entries(EVENTS).map(([key, label]) => (
              <div key={key} className="flex items-center space-x-2">
              <Checkbox
                id={`event-${key}`}
                checked={allowedEvents?.includes(key as AllowedEvent) ?? false}
                onCheckedChange={(checked) => {
                  const currentEvents = allowedEvents ?? []
                  if (checked) {
                    setValue('allowedEvents', [...currentEvents, key as AllowedEvent], {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true
                    })
                  } else {
                    setValue('allowedEvents', currentEvents.filter((e) => e !== key), {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true
                    })
                  }
                }}
                disabled={isLoading}
              />
                <Label htmlFor={`event-${key}`} className="cursor-pointer font-normal">
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('triplewhale.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                {t('triplewhale.setupStep1')}{' '}
                <a
                  href="https://app.triplewhale.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('triplewhale.setupStep1Link')}
                </a>{' '}
                <span dangerouslySetInnerHTML={{ __html: t('triplewhale.setupStep1Suffix') }} />
              </li>
              <li>{t('triplewhale.setupStep2')}</li>
              <li>
                {t('triplewhale.setupStep3')}
                <ul className="ml-4 mt-1 list-inside list-disc space-y-1">
                  <li><code className="rounded bg-muted-foreground/20 px-1 text-xs">Orders:Write</code></li>
                  <li><code className="rounded bg-muted-foreground/20 px-1 text-xs">Subscriptions:Write</code></li>
                </ul>
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('triplewhale.setupStep4') }} />
            </ol>
            </div>
        </CardContent>
      </Card>
    </form>
  )
}
